/**
 * Shared logo sizing — used by TenantLogo, TenantBrand, and TenantIcon so all
 * three size tenant artwork the same way.
 *
 * Sizing combines a context `size` tier with the logo's `shape` (from the
 * registry's `logoSize`). Square badges are given MORE height than wide wordmarks
 * at every tier: a wordmark spreads horizontally, so matching only their heights
 * makes a compact badge look tiny. Sizing by shape normalizes their visual weight.
 *
 * Two representations of the same table:
 *
 * - `LOGO_SIZE_CLASS` — Tailwind classes. The default path, and what every
 *   unscaled call site has always rendered.
 * - `LOGO_SIZE_PX` / `LOGO_MAX_W_PX` — the same numbers, so a per-tenant
 *   `scale` multiplier has something to multiply. Tailwind classes can't be
 *   built at runtime (the JIT only emits classes it can see in source), so a
 *   scaled logo has to go through an inline style.
 *
 * `logoSize()` picks between them: **scale 1 returns the class string
 * untouched**, so the overwhelmingly common case renders byte-identically to
 * before and no existing surface shifts. Only a tenant that opts into a scale
 * gets inline styles.
 */

import type { CSSProperties } from 'react';

export type LogoSizeTier = 'xs' | 'sm' | 'md' | 'lg';
export type LogoShape = 'wide' | 'square';

export const LOGO_SIZE_CLASS: Record<LogoSizeTier, Record<LogoShape, string>> = {
  // xs — compact horizontal header bars (e.g. the web portal's h-16 bar).
  xs: { wide: 'h-8 w-auto max-w-[150px]', square: 'h-11 w-auto max-w-[72px]' },
  sm: { wide: 'h-9 w-auto max-w-[170px]', square: 'h-16 w-auto max-w-[150px]' },
  md: { wide: 'h-12 w-auto max-w-[220px]', square: 'h-20 w-auto max-w-[190px]' },
  lg: { wide: 'h-16 w-auto max-w-[280px]', square: 'h-28 w-auto max-w-[280px]' },
};

/**
 * Rendered height in px for each tier/shape — must stay in lockstep with the
 * `h-*` utilities above (h-8 = 32, h-11 = 44, h-9 = 36, h-16 = 64, h-12 = 48,
 * h-20 = 80, h-28 = 112). `logoSizeTableMatches()` is the guard; the unit test
 * calls it so a divergence fails CI rather than surfacing as a scaled logo
 * that jumps size the moment someone sets `scale`.
 */
export const LOGO_SIZE_PX: Record<LogoSizeTier, Record<LogoShape, number>> = {
  xs: { wide: 32, square: 44 },
  sm: { wide: 36, square: 64 },
  md: { wide: 48, square: 80 },
  lg: { wide: 64, square: 112 },
};

/** Max-width cap in px for each tier/shape — mirrors the `max-w-[…]` utilities. */
export const LOGO_MAX_W_PX: Record<LogoSizeTier, Record<LogoShape, number>> = {
  xs: { wide: 150, square: 72 },
  sm: { wide: 170, square: 150 },
  md: { wide: 220, square: 190 },
  lg: { wide: 280, square: 280 },
};

/** Resolve the Tailwind sizing classes for a size tier + logo shape. */
export function logoSizeClass(size: LogoSizeTier, shape: LogoShape): string {
  return LOGO_SIZE_CLASS[size][shape];
}

/** What a logo component needs to render at a given tier, shape, and scale. */
export interface ResolvedLogoSize {
  className: string;
  /** Present only when `scale` is not 1 — Tailwind can't express a runtime height. */
  style?: CSSProperties;
}

/**
 * Resolve sizing for a tier + shape, optionally scaled.
 *
 * The max-width cap scales alongside the height: it exists to stop a very wide
 * wordmark running off a narrow screen, and holding it fixed while the height
 * grows would just clip the logo the moment anyone scaled up.
 *
 * A non-finite or non-positive scale falls back to 1 rather than emitting
 * `height: NaNpx` — the value can arrive from a hand-edited registry entry, and
 * later from a DB column.
 */
export function logoSize(
  size: LogoSizeTier,
  shape: LogoShape,
  scale = 1,
): ResolvedLogoSize {
  const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;

  if (safeScale === 1) return { className: LOGO_SIZE_CLASS[size][shape] };

  return {
    className: 'w-auto',
    style: {
      height: `${LOGO_SIZE_PX[size][shape] * safeScale}px`,
      maxWidth: `${LOGO_MAX_W_PX[size][shape] * safeScale}px`,
    },
  };
}

/**
 * Guard for the px tables above: every entry must match the `h-*` / `max-w-[…]`
 * utility in `LOGO_SIZE_CLASS`. Exported for the unit test rather than run at
 * module load — this is a source-consistency check, not a runtime concern.
 */
export function logoSizeTableMatches(): boolean {
  const tiers: LogoSizeTier[] = ['xs', 'sm', 'md', 'lg'];
  const shapes: LogoShape[] = ['wide', 'square'];

  return tiers.every((tier) =>
    shapes.every((shape) => {
      const cls = LOGO_SIZE_CLASS[tier][shape];
      // `h-28` → 28 * 4px; Tailwind's spacing scale is 0.25rem per step.
      const height = cls.match(/(?:^|\s)h-(\d+)(?:\s|$)/);
      const maxW = cls.match(/max-w-\[(\d+)px\]/);
      return (
        !!height &&
        !!maxW &&
        Number(height[1]) * 4 === LOGO_SIZE_PX[tier][shape] &&
        Number(maxW[1]) === LOGO_MAX_W_PX[tier][shape]
      );
    }),
  );
}
